<template>
  <div v-click-outside="closeDropdown">
    <div
      class="filter-dropdown__toggle"
      :class="{
        'filter-dropdown__toggle--active': showDropdown,
        'filter-dropdown__toggle--clickable': filteredCategoriesLength > 0
      }"
      @click="toggleDropdown"
    >
      <template v-if="localChartType === 0">
        {{ t('news_page_themes_edit_popup.label.theme') }}
      </template>
      <template v-else-if="localChartType === 1">
        {{ t('news_page_themes_edit_popup.label.rubric') }}
      </template>
      <template v-else-if="localChartType === 2">
        {{ t('news_page_body_post.label.category') }}
      </template>
      ({{ filteredCategoriesLength }})
      <ArrowBottomIcon
        v-if="categories.length"
        class="filter-dropdown__toggle-svg"
        :class="{ 'filter-dropdown__toggle-svg--active': showDropdown }"
      />
    </div>

    <div
      v-if="showDropdown"
      class="filter-dropdown__modal"
    >
      <ChartChooser
        v-if="showChartChooser && localChartType !== 2"
        v-model="localChartType"
        name="ChartFilterDropdown"
      />

      <ListView class="scroll filter-dropdown__modal-list">
        <ListItem
          v-for="item in categoriesToShow"
          :key="item.id"
          v-model="selectedItems"
          :use-checkbox="true"
          :item="item.id"
          :disabled="item.is_disabled && !allEnabled"
        >
          {{ item.name }}
        </ListItem>
      </ListView>

      <ButtonAccentPrimary
        size="large"
        @click="applyFilter"
      >
        {{ t('sentiment_stat_bar.apply') }}
      </ButtonAccentPrimary>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, nextTick, onMounted, ref, toRaw, watch } from "vue";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { useBlockStore } from "@/store/NewsFeed/blocks";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/AvalLocalization";
import ChartChooser from "./ChartChooser.vue";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  chartType: {
    type: Number,
    default: 0
  },
  activeStore: {
    type: Object,
    required: true
  },
  date: {
    type: Array,
    default: () => []
  },
  showChartChooser: {
    type: Boolean,
    default: true
  },
  allEnabled: {
    type: Boolean,
    default: false
  },
  stopLoadOnClear: {
    type: Boolean,
    default: false
  },
  sendParamName: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "update:chartType", "apply", "clear"]);

const rubricsStore = useRubricsStore();
const blockStore = useBlockStore();
const newsStorage = useNewsFeedStore();

const showDropdown = ref(false);
const selectedItems = ref([]);
const localChartType = ref(props.chartType);
const filteredCategories = ref([]);
const activeCategories = ref([]);

// Синхронизация chartType с props
watch(
  () => props.chartType,
  (newVal) => {
    localChartType.value = newVal;
  }
);

watch(
  () => localChartType.value,
  (newVal) => {
    emit("update:chartType", newVal);
  }
);

// Базовые категории в зависимости от типа графика
const categories = computed(() => {
  switch (localChartType.value) {
    case 1:
      return rubricsStore.allRubrics.filter((rubric) => rubric?.is_fixed);
    case 2:
      return newsStorage.filterCategories.list;
    default:
      return blockStore.allThemes;
  }
});

// Количество отфильтрованных категорий
const filteredCategoriesLength = computed(() => {
  if (selectedItems.value?.length) return selectedItems.value.length;
  if (filteredCategories.value?.length) {
    return filteredCategories.value.filter((item) => !item.is_disabled).length;
  }
  return categories.value.length;
});

// Категории для отображения в списке
const categoriesToShow = computed(() => {
  return filteredCategories.value?.length ? filteredCategories.value : categories.value;
});

// Применение фильтра категорий
const applyFilteredCategories = () => {
  const kind = localChartType.value === 0 ? blockStore.allThemes : rubricsStore.allRubrics;

  activeCategories.value = props.activeStore?.items?.map((item) => {
    if (!isNaN(item.name)) {
      return kind.find((theme) => theme.id == item.name)?.name;
    }
    return item.name;
  }) || [];

  filteredCategories.value = [...categories.value].map((item) => {
    item.is_disabled = !activeCategories.value.includes(item.name);
    return item;
  });
};

// Заполнение выбранных категорий
const fillSelectedItems = async () => {
  const rawCategories = toRaw(filteredCategories.value);

  const activeIDs = rawCategories
    .filter((item) => !item.is_disabled || props.allEnabled)
    .map((item) => item.id);

  if (JSON.stringify(selectedItems.value) !== JSON.stringify(activeIDs)) {
    await nextTick();
    selectedItems.value = activeIDs;
  }
};

// Обновление фильтрованных категорий
const refreshFilteredCategories = async () => {
  if (localChartType.value === 2) {
    const ids = props.activeStore?.items?.map((item) => Number(item.name)) || [];
    filteredCategories.value = [...categories.value].map((item) => {
      item.is_disabled = !ids.includes(item.id);
      return item;
    });
  } else {
    applyFilteredCategories();
  }
  await fillSelectedItems();
};

// Закрытие выпадающего списка
const closeDropdown = () => {
  showDropdown.value = false;
};

// Переключение выпадающего списка
const toggleDropdown = () => {
  if (filteredCategoriesLength.value > 0) {
    showDropdown.value = !showDropdown.value;
  }
};

// Применение фильтра
const applyFilter = async () => {
  const selected = toRaw(selectedItems.value);

  if (selected.length === 0) {
    emit("clear");
    if (props.stopLoadOnClear) {
      showDropdown.value = false;
      return;
    }
  }

  // Определяем имя параметра для API
  let paramName;
  switch (localChartType.value) {
    case 1:
      paramName = "rubricIDs";
      break;
    case 2:
      paramName = "categoryIDs";
      break;
    default:
      paramName = "blockIDs";
      break;
  }

  // Загружаем данные через store
  if (props.date?.length) {
    if (props.sendParamName) {
      await props.activeStore.loadStat(props.date[0], props.date[1], paramName, selected);
    } else {
      await props.activeStore.loadStat(props.date[0], props.date[1], selected);
    }
  } else {
    await props.activeStore.loadStat(selected);
  }

  emit("update:modelValue", selected);
  emit("apply", selected);
  showDropdown.value = false;
};

// Следим за изменением типа графика и данных store
watch(
  () => localChartType.value,
  () => {
    applyFilteredCategories();
    fillSelectedItems();
  }
);

watch(
  () => newsStorage.filterCategories.list.length,
  async () => {
    await refreshFilteredCategories();
  },
  { deep: false }
);

// Инициализация при монтировании
onMounted(() => {
  nextTick(async () => {
    await refreshFilteredCategories();
  });
});
</script>

<style lang="scss" scoped>
.filter-dropdown {
  &__toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #F7F8FD;
    padding: 14px;
    border-radius: 8px;
    user-select: none;
    width: 150px;

    &-svg {
      color: var(--text-text-pur);
      transition: .2s;
      margin-left: auto;

      &--active {
        transform: rotate(180deg);
        color: #1F9AFF;
      }
    }

    &--active {
      color: #1F9AFF;
      border: #EEF1FF;
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &__modal {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    max-width: 330px;
    max-height: 379px;
    overflow-x: hidden;
    background: var(--background-bg-white);
    z-index: 40;
    position: absolute;
    top: 45px;
    right: 26px;
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 9px 28px 8px #0000001a;
    line-height: 18px;
    text-align: left;
    user-select: none;

    &-list {
      max-height: 290px;
    }
  }
}
</style>
